import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Wordmark, PrivacyNotice } from "../components/Brand";
import { apiError } from "../lib/api";
import { Lock, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err, "Sign in failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex relative overflow-hidden bg-navy text-white flex-col justify-between p-12">
        <img src="https://ckingeducation.com/hero/painterly-bg.jpeg" alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-navy/60" />
        <div className="relative fade-up">
          <Wordmark light size="text-2xl" />
          <div className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-white/60 flex items-center gap-1.5">
            <Lock size={11} /> Operations Hub — Internal
          </div>
        </div>
        <div className="relative fade-up-2">
          <h1 className="font-serif text-4xl xl:text-5xl font-bold leading-tight">
            Building Thinkers<br />Through Mathematics
          </h1>
          <p className="mt-5 text-white/80 max-w-md leading-relaxed">
            The private operations workspace for CKingEducation consultants — daily service records,
            the Operations Agent, and approval-ready follow-ups.
          </p>
        </div>
        <div className="relative text-xs text-white/50 fade-up-3">
          Private system. Access is limited to CKingEducation consultants and Admins.
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden mb-8">
            <Wordmark size="text-2xl" />
            <div className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#78716C] flex items-center gap-1.5">
              <Lock size={11} /> Operations Hub — Internal
            </div>
          </div>
          <p className="eyebrow">Secure sign in</p>
          <h2 className="section-title text-3xl mt-2 mb-8">Welcome back</h2>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="label" htmlFor="login-email">Email</label>
              <input id="login-email" data-testid="login-email-input" type="email" required
                className="input" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@ckinged.com" autoComplete="email" />
            </div>
            <div>
              <label className="label" htmlFor="login-password">Password</label>
              <div className="relative">
                <input id="login-password" data-testid="login-password-input" type={showPassword ? "text" : "password"} required
                  className="input w-full !pr-11" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" autoComplete="current-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} data-testid="login-password-toggle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-navy transition-colors"
                  title={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              <div className="text-right mt-1.5">
                <Link to="/forgot-password" className="text-xs font-bold text-terracotta hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
            </div>
            {error && (
              <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm font-bold text-[#991B1B]"
                data-testid="login-error">{error}</div>
            )}
            <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="login-submit-button">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-8"><PrivacyNotice /></div>
          <p className="mt-6 text-xs text-[#78716C] leading-relaxed" data-testid="login-access-note">
            Invite-only. Principals, teachers, coaches, district leaders, members, and public visitors
            cannot sign in here. This app is separate from the public CKingEducation website.
          </p>
        </div>
      </div>
    </div>
  );
}
